import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowRight, Compass, MapPin, RefreshCw, Share2, Sparkles, Mail, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliatePartners, AffiliatePartner } from '@/hooks/useAffiliatePartners';
import { AffiliatePartnerCard } from '@/components/affiliate/AffiliatePartnerCard';
import { toast } from 'sonner';

interface QuizResultsProps {
  answers: Record<string, { value: string; tags: string[] }>;
  tags: string[];
  sessionId: string;
  onRestart: () => void;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string;
}

interface Itinerary {
  id: string;
  title: string;
  slug: string;
  destination: string;
  duration: number;
  featured_image: string;
}

const TRAVEL_STYLES: Record<string, { title: string; description: string; emoji: string }> = {
  adventure: {
    title: 'The Adventurer',
    description: 'You crave thrills and excitement! From mountain peaks to jungle treks, you\'re happiest when your heart is racing.',
    emoji: '🏔️',
  },
  relaxation: {
    title: 'The Zen Seeker',
    description: 'You travel to recharge and find peace. Beach days, spa treatments, and scenic views are your ultimate rewards.',
    emoji: '🧘',
  },
  culture: {
    title: 'The Culture Explorer',
    description: 'Museums, historical sites, and local traditions fascinate you. Every trip is a journey through time and heritage.',
    emoji: '🏛️',
  },
  foodie: {
    title: 'The Food Nomad',
    description: 'Your stomach leads the way! Street food, fine dining, and local cuisines are your travel highlights.',
    emoji: '🍜',
  },
};

export const QuizResults = ({ answers, tags, sessionId, onRestart }: QuizResultsProps) => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  
  // New state for tracking quiz result save
  const [quizResultId, setQuizResultId] = useState<string | null>(null);
  const [quizSaveError, setQuizSaveError] = useState<string | null>(null);
  const [quizSaving, setQuizSaving] = useState(true);
  
  const { data: allPartners } = useAffiliatePartners();

  const travelStyle = TRAVEL_STYLES[answers.travel_style?.value] || TRAVEL_STYLES.adventure;

  const handleSaveEmail = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    if (!quizResultId) {
      toast.error('Quiz result not saved yet. Please wait and try again.');
      return;
    }
    
    setSavingEmail(true);
    console.log('Attempting to save email for quiz result ID:', quizResultId);
    
    try {
      // Use the stored row ID instead of session_id for more reliable updates
      const { data, error } = await supabase
        .from('quiz_results')
        .update({ email })
        .eq('id', quizResultId)
        .select();
      
      console.log('Email update result:', { data, error });
      
      if (error) {
        console.error('Email save error:', error);
        toast.error('Failed to save email: ' + error.message);
        return;
      }
      
      if (!data || data.length === 0) {
        console.error('No matching record found for ID:', quizResultId);
        toast.error('Could not find your quiz result to save email');
        return;
      }
      
      setEmailSaved(true);
      toast.success('Email saved! We\'ll send you personalized recommendations.');
    } catch (error) {
      console.error('Email save error:', error);
      toast.error('Failed to save email');
    } finally {
      setSavingEmail(false);
    }
  };

  // Retry saving quiz result
  const handleRetryQuizSave = async () => {
    setQuizSaving(true);
    setQuizSaveError(null);
    await saveQuizResult();
  };

  // Save quiz result function
  const saveQuizResult = async () => {
    try {
      console.log('Saving quiz result with sessionId:', sessionId);
      
      const { data: insertData, error: insertError } = await supabase
        .from('quiz_results')
        .insert({
          session_id: sessionId,
          travel_style: answers.travel_style?.value || 'adventure',
          budget_preference: answers.budget?.value,
          activity_level: answers.activity_level?.value,
          trip_duration: answers.trip_duration?.value,
          recommended_tags: tags,
        })
        .select();
      
      console.log('Quiz result insert:', { insertData, insertError });
      
      if (insertError) {
        console.error('Failed to save quiz result:', insertError);
        setQuizSaveError(insertError.message);
        setQuizSaving(false);
        return false;
      }
      
      if (insertData && insertData.length > 0) {
        setQuizResultId(insertData[0].id);
        console.log('Quiz result saved with ID:', insertData[0].id);
        setQuizSaving(false);
        return true;
      }
      
      setQuizSaveError('No data returned from insert');
      setQuizSaving(false);
      return false;
    } catch (error) {
      console.error('Error saving quiz result:', error);
      setQuizSaveError(error instanceof Error ? error.message : 'Unknown error');
      setQuizSaving(false);
      return false;
    }
  };

  // Filter partners based on quiz answers
  const getRecommendedPartners = (): AffiliatePartner[] => {
    if (!allPartners) return [];
    
    const categoryMap: Record<string, string[]> = {
      adventure: ['Tours', 'Activities', 'Insurance'],
      relaxation: ['Hotels', 'Resorts', 'Insurance'],
      culture: ['Tours', 'Activities', 'Flights'],
      foodie: ['Tours', 'Activities', 'Hotels'],
    };

    const preferredCategories = categoryMap[answers.travel_style?.value] || [];
    
    return allPartners
      .filter((p) => preferredCategories.some((cat) => 
        p.category.toLowerCase().includes(cat.toLowerCase())
      ))
      .slice(0, 3);
  };

  const recommendedPartners = getRecommendedPartners();

  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoading(true);
      
      try {
        // First save the quiz result and get the ID
        await saveQuizResult();

        // Fetch matching blog posts by tags
        const { data: tagData } = await supabase
          .from('tags')
          .select('id, name')
          .in('name', tags);

        if (tagData && tagData.length > 0) {
          const tagIds = tagData.map((t) => t.id);
          
          const { data: postTags } = await supabase
            .from('blog_posts_tags')
            .select('blog_post_id')
            .in('tag_id', tagIds);

          if (postTags && postTags.length > 0) {
            const postIds = [...new Set(postTags.map((pt) => pt.blog_post_id))];
            
            const { data: posts } = await supabase
              .from('blog_posts')
              .select('id, title, slug, excerpt, featured_image')
              .in('id', postIds)
              .eq('status', 'published')
              .limit(4);

            if (posts) setBlogPosts(posts);
          }

          // Fetch matching itineraries
          const { data: itinTags } = await supabase
            .from('itinerary_tags')
            .select('itinerary_id')
            .in('tag_id', tagIds);

          if (itinTags && itinTags.length > 0) {
            const itinIds = [...new Set(itinTags.map((it) => it.itinerary_id))];
            
            const { data: itins } = await supabase
              .from('itineraries')
              .select('id, title, slug, destination, duration, featured_image')
              .in('id', itinIds)
              .eq('status', 'published')
              .limit(3);

            if (itins) setItineraries(itins);
          }
        }

        // Fallback: get recent posts if no tag matches
        if (blogPosts.length === 0) {
          const { data: recentPosts } = await supabase
            .from('blog_posts')
            .select('id, title, slug, excerpt, featured_image')
            .eq('status', 'published')
            .order('created_at', { ascending: false })
            .limit(4);

          if (recentPosts) setBlogPosts(recentPosts);
        }
      } catch (error) {
        console.error('Error fetching recommendations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [sessionId, answers, tags]);

  const handleShare = async () => {
    const shareData = {
      title: `I'm "${travelStyle.title}" - What's Your Travel Style?`,
      text: `I just took the Travel Style Quiz and discovered I'm "${travelStyle.title}"! Take the quiz to find your travel personality.`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <>
      <Helmet>
        <title>Your Travel Style: {travelStyle.title} | Quiz Results</title>
      </Helmet>

      <div className="container px-4 sm:px-6 mx-auto py-8 max-w-4xl">
        {/* Result Header */}
        <Card className="mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-8 text-center">
            <span className="text-6xl mb-4 block">{travelStyle.emoji}</span>
            <Badge variant="secondary" className="mb-4">Your Travel Style</Badge>
            <h1 className="text-3xl font-bold mb-4">{travelStyle.title}</h1>
            <p className="text-muted-foreground max-w-lg mx-auto">{travelStyle.description}</p>
            
            <div className="flex flex-wrap gap-2 justify-center mt-6">
              {tags.slice(0, 6).map((tag) => (
                <Badge key={tag} variant="outline">{tag}</Badge>
              ))}
            </div>
          </div>
          
          <CardContent className="p-6 space-y-4">
            {/* Error alert if quiz save failed */}
            {quizSaveError && (
              <Alert variant="destructive" className="max-w-md mx-auto">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>Failed to save your results. </span>
                  <Button variant="outline" size="sm" onClick={handleRetryQuizSave}>
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Email capture - only show when quiz is saved successfully */}
            {quizSaving ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving your results...</span>
              </div>
            ) : !emailSaved && quizResultId ? (
              <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Get personalized tips (optional)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && email) {
                        handleSaveEmail();
                      }
                    }}
                  />
                </div>
                <Button onClick={handleSaveEmail} disabled={savingEmail || !email}>
                  {savingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            ) : emailSaved ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-green-500" />
                <span>Email saved!</span>
              </div>
            ) : null}
            
            <div className="flex flex-wrap gap-3 justify-center">
              <Button variant="outline" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share Result
              </Button>
              <Button variant="ghost" onClick={onRestart}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retake Quiz
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recommended Partners */}
        {recommendedPartners.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold">Recommended for {travelStyle.title}s</h2>
            </div>
            <div className="space-y-4">
              {recommendedPartners.map((partner) => (
                <AffiliatePartnerCard key={partner.id} partner={partner} compact />
              ))}
            </div>
          </section>
        )}

        {/* Matching Itineraries */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-3 mb-8">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-lg" />
            ))}
          </div>
        ) : itineraries.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Perfect Itineraries for You</h2>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/itineraries">View All <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {itineraries.map((itin) => (
                <Link key={itin.id} to={`/itineraries/${itin.slug}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                    {itin.featured_image && (
                      <img
                        src={itin.featured_image}
                        alt={itin.title}
                        className="w-full h-32 object-cover"
                      />
                    )}
                    <CardContent className="p-4">
                      <h3 className="font-semibold line-clamp-2">{itin.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {itin.destination} • {itin.duration} days
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Matching Blog Posts */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : blogPosts.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Compass className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Stories You'll Love</h2>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/blog">View All <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {blogPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                    <div className="flex">
                      {post.featured_image && (
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-24 h-24 object-cover"
                        />
                      )}
                      <CardContent className="p-4 flex-1">
                        <h3 className="font-semibold line-clamp-2">{post.title}</h3>
                        {post.excerpt && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{post.excerpt}</p>
                        )}
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
};
